// reach_ui/src/components/VastGPT/redux.js
import ActionType from '../../actions/ActionType'
import { create, update } from '../../actions/data'
import { OPEN_AI_URL, DATA_API_URL } from '../../utils/AllURLs'
import { combineReducers } from 'redux'
import cosineSimilarity from '../../utils/cosineSimilarity'

export const addRelevantInfo = payload => ({
  type: 'VastGPT.ADD_RELEVANT_INFO',
  payload,
})

export const addRelevantInfoInbox = payload => ({
  type: 'VastGPT.ADD_RELEVANT_INFO_INBOX',
  payload,
})

export const clearRelevantInfo = () => ({
  type: 'VastGPT.CLEAR_RELEVANT_INFO',
})

export const setRelevantInfoExpanded = payload => ({
  type: 'VastGPT.SET_RELEVANT_INFO_EXPANDED',
  payload,
})

export const selectChat = chatId => (dispatch, getState) => {
  const currentChatId = getState().VastGPT.selectedChatId
  if (chatId === currentChatId) {
    return
  }

  if (chatId) {
    dispatch({ type: 'VastGPT.SELECT_CHAT', payload: chatId })
  } else {
    dispatch(receiveChat(null))
    dispatch({ type: 'VastGPT.SELECT_CHAT', payload: null })
  }
}

export const clearSelectedChat = () => ({
  type: 'VastGPT.CLEAR_SELECTED_CHAT',
})

export const addMessage = ({ chatId, message }) => ({
  type: 'VastGPT.ADD_MESSAGE',
  payload: { chatId, message },
})

export const replaceMessage = ({ chatId, message, index }) => ({
  type: 'VastGPT.REPLACE_MESSAGE',
  payload: { chatId, message, index },
})

export const clearMessages = () => ({
  type: 'VastGPT.CLEAR_MESSAGES',
})

export const setLoading = payload => ({
  type: 'VastGPT.SET_LOADING',
  payload,
})

export const receiveChat = payload => ({
  type: 'VastGPT.RECEIVE_CHAT',
  payload,
})

export const relevantInfoFunc =
  ({ embedding, type }) =>
  (dispatch, getState) =>
    new Promise((resolve, reject) => {
      dispatch(setRelevantInfoExpanded(true))

      const embeddingsArray = Object.values(getState().data?.knowledge || {})

      let unsortedArr = []

      for (let i = 0; i < embeddingsArray.length; i++) {
        const similarity = cosineSimilarity(
          embedding,
          embeddingsArray[i].embedding,
        )

        if (similarity >= 0.8) {
          if (type === 'inbox') {
            dispatch(
              addRelevantInfoInbox({
                title: embeddingsArray[i]?.title,
                id: embeddingsArray[i]?.id,
                similarity,
              }),
            )
          } else {
            dispatch(
              addRelevantInfo({
                title: embeddingsArray[i]?.title,
                id: embeddingsArray[i]?.id,
                similarity,
              }),
            )
          }

          unsortedArr.push({
            content: embeddingsArray[i].content,
            title: embeddingsArray[i].title,
            id: embeddingsArray[i].id,
            similarity,
          })
        }

        if (i === embeddingsArray.length - 1) {
          dispatch(setRelevantInfoExpanded(false))
        }
      }

      const sortedArr = unsortedArr.sort((a, b) => {
        if (a.similarity < b.similarity) {
          return -1
        } else if (a.similarity > b.similarity) {
          return 1
        }
        return 0
      })

      let relevantInfoArr = sortedArr
      let relevantInformation = JSON.stringify(sortedArr)

      resolve({ relevantInformation, relevantInfoArr })
    })

export const createEmbedding = content => (dispatch, getState) =>
  new Promise((resolve, reject) => {
    return dispatch(
      create({
        entity: { prompt: content, command: 'embedding' },
        entityType: 'openAiEmbedding',
        hasSchema: false,
        url: OPEN_AI_URL,
        snackbarMessage: 'none',
      }),
    ).then(payload => resolve(payload?.data?.[0]?.embedding))
  })

/*
This shouldn't get knowledge if it's already loaded.
For now we can assume that knowledge is loaded since
it's only shown on the knowledge page
*/

const initialPrompt = ({ relevantInformation }) =>
  `A user is searching for knowledge or giving a command.
            
  ${
    relevantInformation?.length
      ? `Here is an array of some potentially relevant knowledge, and the most relevant knowledge is at the end of the array:
      \`\`\`
      ${relevantInformation}
      \`\`\`
      `
      : ''
  }
  
  In your response do not include any acknowledgement that you are an AI Language model.
  Do not reference programming nor use terms like "array," "object," or "markdown," unless a user explicitly asks for code or unless the term is being used in a non-programming context. 
  Utilize markdown, code snippets, links, and images in your response.
  `

export const sendMessageToOpenAi =
  ({ content, chatId, firstMessage }) =>
  async (dispatch, getState) => {
    const idTokenPayload = getState().auth?.session?.idToken?.payload
    const given_name = idTokenPayload?.given_name.replace(/[^a-zA-Z0-9]/g, '')
    const family_name = idTokenPayload?.family_name.replace(/[^a-zA-Z0-9]/g, '')

    console.log(
      `sendMessageToOpenAi({ content: ${content}, chatId: ${chatId}, firstMessage: ${firstMessage} })`,
    )

    const send = async () => {
      if (chatId) {
        await dispatch(
          update(
            {
              id: chatId,
              messages: getState().VastGPT.messages,
              status: 'REQUEST',
              lastUpdateAttempt: Math.floor(Date.now() / 1000),
            },
            'openaichat',
            null,
            '',
            null,
            true,
            'none',
            `${DATA_API_URL}/openaichat/${chatId}`,
          ),
        )
      } else {
        const createPayload = await dispatch(
          create({
            entity: {
              messages: getState().VastGPT.messages,
              status: 'REQUEST',
              lastUpdateAttempt: Math.floor(Date.now() / 1000),
            },
            entityType: 'openaichat',
            hasSchema: false,
            url: `${DATA_API_URL}/openaichat`,
            snackbarMessage: 'none',
          }),
        )
        chatId = createPayload.id
        dispatch(selectChat(chatId))
      }
    }

    if (firstMessage || !chatId) {
      dispatch(clearMessages())
      dispatch(clearRelevantInfo())
      dispatch(setLoading('initial'))

      const embedding = await dispatch(createEmbedding(content))
      const relevantInfo = await dispatch(relevantInfoFunc({ embedding }))
      const { relevantInformation } = relevantInfo

      dispatch(
        addMessage({
          chatId,
          message: {
            role: 'system',
            content: initialPrompt({ relevantInformation }),
          },
        }),
      )

      dispatch(
        addMessage({
          chatId,
          message: {
            role: 'user',
            content,
            name: `${given_name}_${family_name}`,
          },
        }),
      )

      return send()
    } else {
      setLoading('loading')
      const messages = getState().VastGPT.messages
      const lastMessage = messages[messages.length - 1]

      if (lastMessage.tool_calls) {
        dispatch(
          addMessage({
            chatId,
            message: {
              role: 'tool',
              name: lastMessage.tool_calls[0]?.function?.name,
              tool_call_id: lastMessage.tool_calls[0]?.id,
              content: lastMessage.tool_calls[0]?.function?.arguments,
            },
          }),
        )
      }

      dispatch(
        addMessage({
          chatId,
          message: {
            role: 'user',
            content,
            user: `${given_name}_${family_name}`,
          },
        }),
      )
      return send()
    }
  }

const messages = (state = [], action) => {
  const switchObj = {
    'VastGPT.ADD_MESSAGE': () => {
      return [...state, action.payload.message]
    },
    'VastGPT.REPLACE_MESSAGE': () => {
      return [...state.slice(0, -1), action.payload.message]
    },
    'VastGPT.CLEAR_MESSAGES': () => {
      return []
    },
    'VastGPT.RECEIVE_CHAT': () => {
      return action.payload?.messages || []
    },
  }

  // If the function returns falsey, a ternary is required because
  // if `|| state` is used, state will remain the same
  return switchObj?.[action?.type] ? switchObj[action?.type]() : state
}

const loadingResponse = (state = false, action) => {
  const switchObj = {
    'VastGPT.SET_LOADING': () => {
      return action.payload
    },
  }

  // If the function returns falsey, a ternary is required because
  // if `|| state` is used, state will remain the same
  return switchObj?.[action?.type] ? switchObj[action?.type]() : state
}

const relevantInfo = (state = [], action) => {
  const switchObj = {
    'VastGPT.ADD_RELEVANT_INFO': () => {
      return [...state, action.payload]
    },
    'VastGPT.CLEAR_RELEVANT_INFO': () => {
      return []
    },
    'VastGPT.SELECT_CHAT': () => {
      return []
    },
  }

  // If the function returns falsey, a ternary is required because
  // if `|| state` is used, state will remain the same
  return switchObj?.[action?.type] ? switchObj[action?.type]() : state
}

const relevantInfoExpanded = (state = false, action) => {
  const switchObj = {
    'VastGPT.SET_RELEVANT_INFO_EXPANDED': () => {
      return action.payload
    },
  }

  // If the function returns falsey, a ternary is required because
  // if `|| state` is used, state will remain the same
  return switchObj?.[action?.type] ? switchObj[action?.type]() : state
}

const selectedChatId = (state = null, action) => {
  switch (action.type) {
    case 'VastGPT.SELECT_CHAT':
      return action.payload
    case 'VastGPT.CLEAR_SELECTED_CHAT':
      return null
    case ActionType.View.TOGGLE_DIALOG:
      if (action.payload.dialog === 'AiKnowledge') {
        return null
      }
      return state
    default:
      return state
  }
}

export default combineReducers({
  selectedChatId,
  messages,
  loadingResponse,
  relevantInfo,
  relevantInfoExpanded,
})